import React, { useEffect, useState } from "react";
import { CheckCircle } from 'lucide-react';
import Swal from 'sweetalert2';
import { uri } from "constants/common";


const BilingualExamForm = () => {
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        email: "",
        fullName: "",
        phone: "",
        language: "",
        examFormat: "",
        previousExam: "",
        retakeSection: "",
        otherRetake: "", // initialize here
        examReason: "",
    });

    const [errors, setErrors] = useState({});
    const [languages, setLanguages] = useState([]);

    const handleInputChange = (name, value) => {
        setFormData({
            ...formData,
            [name]: value,
        });

        // Only clear the error if the field is valid
        if (errors[name]) {
            let isValid = true;

            if (name === "email") {
                // Email must be non-empty and valid
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                isValid = value.trim() !== "" && emailRegex.test(value);
            } else if (name === "otherRetake") {
                isValid = value.trim() !== "";
            } else {
                isValid = value.trim() !== "";
            }

            if (isValid) {
                setErrors({
                    ...errors,
                    [name]: undefined, // error only removed if valid
                });
            }
        }
    };

    useEffect(() => {
        const fetchLanguages = async () => {
            try {
                const response = await fetch( uri + '/get-languages');
                const data = await response.json();
                if (data.success) {
                    setLanguages(data.data); // Store the fetched language data
                }
            } catch (error) {
                console.error('Error fetching languages:', error);
            }
        };

        fetchLanguages();
    }, []);


    const validateForm = () => {
        const newErrors = {};

        // Email validation
        if (!formData.email.trim()) {
            newErrors.email = "Email is required";
        } else {
            // simple email regex
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(formData.email)) {
                newErrors.email = "Please enter a valid email address";
            }
        }

        if (!formData.fullName) newErrors.fullName = "Name is required";

        if (!formData.language) newErrors.language = "Language is required";
        if (!formData.examFormat)
            newErrors.examFormat = "Exam format is required";
        if (!formData.previousExam)
            newErrors.previousExam = "Please select an option";

        if (formData.previousExam === "Retake" && !formData.retakeSection) {
            newErrors.retakeSection = "Please select a retake section";
        }

        if (!formData.examReason)
            newErrors.examReason = "Please select a reason";
        if (formData.previousExam === "Retake") {
            if (!formData.retakeSection) {
                newErrors.retakeSection = "Please select a retake section";
            } else if (
                formData.retakeSection === "Other" &&
                !formData.otherRetake?.trim()
            ) {
                newErrors.otherRetake = "Please specify the retake portion";
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };


    const handleSubmit = async () => {
        if (!validateForm()) return;
        setLoading(true);

        try {
            const apiPayload = {
                type: 2,
                email: formData.email,
                contact_person_name: formData.fullName,
                additional_contact_number: formData.phone,
                language_id: formData.language,
                meeting_format: formData.examFormat,
                previous_exam: formData.previousExam,
                retake_section: formData.retakeSection === "Other" ? formData.otherRetake : formData.retakeSection,
                exam_reason: formData.examReason,
            };

            const response = await fetch(
                uri+"/interpreting-service-request/generate",
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(apiPayload),
                }
            );

            const result = await response.json();

            if (response.ok) {
                setLoading(false);
                Swal.fire({
                    title: "Success!",
                    text: 'Exam request submitted successfully. Your ID is #' + result.message,
                    icon: "success",
                    confirmButtonText: "OK",
                }).then(() => {
                    setFormData({
                        email: "",
                        fullName: "",
                        phone: "",
                        language: "",
                        examFormat: "",
                        previousExam: "",
                        retakeSection: "",
                        otherRetake: "",
                        examReason: "",
                    });
                });
                console.log("API Response:", result);
            } else {
                setLoading(false);
                const errorMessages = result?.errors
                    ? Object.values(result.errors).flat().join('\n')
                    : null;
                Swal.fire({
                    title: "Error!",
                    text: errorMessages || result?.message || "Submission failed. Please try again.",
                    icon: "error",
                    confirmButtonText: "OK",
                });
            }
        } catch (error) {
                setLoading(false);
            Swal.fire({
                title: "Network Error",
                text: "There was a network error. Please try again later.",
                icon: "error",
                confirmButtonText: "OK",
            });
            console.error("Error submitting form:", error);
        }
    };

    return (
        <div className="app-container">
            <div className="header">
                <div className="logo">
                    <div className="logo_wrapper">
                        <img
                            src="/images/logo-fill.png"
                            alt="ALGOVI Logo"
                            className="logo-img"
                        />
                    </div>
                </div>
                <h1 className="main-title">
                    Bilingual Exam
                </h1>
            </div>

            <div className="form-container">
                <div className="form-card">
                    <div>
                        <div className="step-content">
                            <div className="step-header">
                                <h2 className="step-title">Appointment Form For Bilingual Exam </h2>
                                <p className="step-description">
                                    Please complete this form to request a Bilingual Exam if the event is beyond the school day (after 4:00 pm) from the Language Development Office .
                                    <br />
                                    <br />
                                    To schedule an exam, please submit the form. Please submit your request as quickly as possible as we schedule on a first come, first served basis.
                                    <br />
                                    <br />
                                    PROCEDURES OF SCHEDULING AND CANCELING:
                                    <br />
                                    Exams are scheduled out 3 weeks in advance or sooner.
                                    <br />
                                    To cancel and reschedule, please contact: <strong> Language Development Office</strong>
                                    <br />
                                    Last minute cancellations: <strong>Please contact us 24 hours in advance to cancel the exam</strong>.
                                    <br />
                                    After the assessment is taken, results can take up to two weeks.
                                    <br />
                                    <br />
                                    Language Development Office
                                    <br />
                                    56 S.  Lincoln Street Stockton, Ca 95203
                                    <br />
                                    Phone: (209) 933‐7074 ext.2427 or ext.2431
                                    <br />
                                    <br />
                                     <span className='required'>* Indicates required question</span>
                                </p>
                            </div>

                            {/* Email */}
                            <div className="form-group">
                                <label className="form-label">
                                    Email <span className="required">*</span>
                                </label>
                                <input
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => handleInputChange("email", e.target.value)}
                                    className={`form-input ${errors.email ? "error" : ""}`}
                                    placeholder="Enter your email"
                                />
                                {errors.email && (
                                    <p className="error-message">{errors.email}</p>
                                )}
                            </div>

                            {/* Full Name */}
                            <div className="form-group">
                                <label className="form-label">
                                    First and Last Name <span className="required">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={formData.fullName}
                                    onChange={(e) =>
                                        handleInputChange("fullName", e.target.value)
                                    }
                                    className={`form-input ${errors.fullName ? "error" : ""
                                        }`}
                                    placeholder="Enter your full name"
                                />
                                {errors.fullName && (
                                    <p className="error-message">{errors.fullName}</p>
                                )}
                            </div>

                            {/* Phone */}
                            <div className="form-group">
                                <label className="form-label">
                                    Best phone number to contact
                                </label>
                                <input
                                    type="tel"
                                    value={formData.phone}
                                    onChange={(e) => handleInputChange("phone", e.target.value)}
                                    className="form-input"
                                    placeholder="(209) 555-1234"
                                />
                            </div>

                            {/* Language */}
                            <div className="form-group">
                                <label className="form-label">
                                    Which language are you seeking certification/proficiency?
                                    <span className="required">*</span>
                                </label>
                                <select
                                    value={formData.language || ""}
                                    onChange={(e) => handleInputChange("language", e.target.value)}
                                    className={`form-input form-select ${errors.language ? "error" : ""}`}
                                >
                                    <option value="">-- Select Language --</option>
                                    {languages.map((lang) => (
                                        <option key={lang.id} value={lang.id}>
                                            {lang.name}
                                        </option>
                                    ))}
                                </select>
                                {errors.language && (
                                    <p className="error-message">{errors.language}</p>
                                )}
                            </div>

                            {/* Exam Format */}
                            <div className="form-group">
                                <label className="form-label">
                                    Format for Meeting <span className="required">*</span>
                                </label>
                                <select
                                    value={formData.examFormat}
                                    onChange={(e) =>
                                        handleInputChange("examFormat", e.target.value)
                                    }
                                    className={`form-input form-select ${errors.examFormat ? "error" : ""
                                        }`}
                                >
                                    <option value="">-- Select Format --</option>
                                    <option value="Online Avant (for certified positions)">
                                        Online Avant (for certified positions)
                                    </option>
                                    <option value="Bilingual assistant/paraprofessional">
                                        Bilingual assistant/paraprofessional
                                    </option>
                                    <option value="Interpreter/Translator Specialist">
                                        Interpreter/Translator Specialist
                                    </option>
                                    <option value="Police Officer">
                                        Police Officer
                                    </option>
                                    <option value="Braille">
                                        Braille
                                    </option>
                                </select>
                                {errors.examFormat && (
                                    <p className="error-message">{errors.examFormat}</p>
                                )}
                            </div>

                            {/* Previous Exam */}
                            <div className="form-group">
                                <label className="form-label">
                                    Have you taken a Bilingual Exam with the District before?
                                    <span className="required">*</span>
                                </label>
                                <select
                                    value={formData.previousExam}
                                    onChange={(e) =>
                                        handleInputChange("previousExam", e.target.value)
                                    }
                                    className={`form-input form-select ${errors.previousExam ? "error" : ""
                                        }`}
                                >
                                    <option value="">-- Select --</option>
                                    <option value="Yes">Yes</option>
                                    <option value="No">No</option>
                                    <option value="Retake">I need to retake a section</option>
                                </select>
                                {errors.previousExam && (
                                    <p className="error-message">{errors.previousExam}</p>
                                )}
                            </div>

                            {/* Retake Portion */}
                            {formData.previousExam === "Retake" && (
                                <div className="form-group">
                                    <label className="form-label">
                                        If retake, what portion? <span className="required">*</span>
                                    </label>
                                    <select
                                        value={formData.retakeSection}
                                        onChange={(e) =>
                                            handleInputChange("retakeSection", e.target.value)
                                        }
                                        className={`form-input form-select ${errors.retakeSection ? "error" : ""}`}
                                    >
                                        <option value="">-- Select Portion --</option>
                                        <option value="Writing">Writing</option>
                                        <option value="Reading">Reading</option>
                                        <option value="Oral">Oral (speaking and listening)</option>
                                        <option value="Other">Other</option>
                                    </select>
                                    {errors.retakeSection && (
                                        <p className="error-message">{errors.retakeSection}</p>
                                    )}

                                    {/* Show text input if "Other" is selected */}
                                    {formData.retakeSection === "Other" && (
                                        <div className="form-group" style={{ marginTop: "0.5rem" }}>
                                            <label className="form-label">
                                                Please specify <span className="required">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={formData.otherRetake || ""}
                                                onChange={(e) => handleInputChange("otherRetake", e.target.value)}
                                                className={`form-input ${errors.otherRetake ? "error" : ""}`}
                                                placeholder="Specify the portion"
                                            />
                                            {errors.otherRetake && (
                                                <p className="error-message">{errors.otherRetake}</p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}


                            {/* Exam Reason */}
                            <div className="form-group">
                                <label className="form-label">
                                    Why are you taking this exam? <span className="required">*</span>
                                </label>
                                <select
                                    value={formData.examReason}
                                    onChange={(e) =>
                                        handleInputChange("examReason", e.target.value)
                                    }
                                    className={`form-input form-select ${errors.examReason ? "error" : ""
                                        }`}
                                >
                                    <option value="">-- Select Reason --</option>
                                    <option value=" I'm interested in a job that requires the Bilingual Proficiency Exam">
                                        I'm interested in a job that requires the Bilingual Proficiency Exam
                                    </option>
                                    <option value="I want to show I am bilingual">
                                        I want to show I am bilingual (e.g., Bilingual Stipend for SUSD employees)
                                    </option>
                                </select>
                                {errors.examReason && (
                                    <p className="error-message">{errors.examReason}</p>
                                )}
                            </div>
                        </div>

                        {/* Submit Button */}
                        <div className="form-group" style={{ textAlign: "center" }}>
                            <button onClick={handleSubmit} type="submit" className="nav-button success">
                                Submit Request
                                <CheckCircle size={20} style={{ marginLeft: "0.5rem" }} />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            
             <div className="copy_right" style={{ textAlign: 'center', margin: '2rem', fontSize: '15px', lineHeight: '1.5' }}>
                    <p>Copyright 2025 <a href="https://algovisolutions.com/">Algovi Services LLC.</a> All rights reserved</p>
            </div>      
            {loading && (
      <div className="loader_wrapper">
        <img src="/images/form_loader.svg" alt="" />
      </div>
    )}               
            {/* Same CSS as before */}
            <style jsx>{`
        .app-container {
          min-height: 100vh;
          display: flex;
          justify-content: center;
          align-items: center;
          background: linear-gradient(#6f8dff, #57ccf3);
          padding: 1rem;
          flex-direction: column;
        }
        .step-description {
          color: #000000ff;
          line-height: 1.4;
          text-align: start;

        }
        .form-group {
          margin-bottom: 1.5rem;
        }
        .step-title {
          font-size: 1.8rem;
          font-weight: bold;
          color: #1f2937;
          margin-bottom: 0.8rem;
          line-height: 1.3;
          text-align: start;
        }
        .step-header {
          text-align: center;
          margin-bottom: 2rem;
        }
        .header {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          gap: 10px;
        }
        .logo_wrapper {
          margin-bottom: 7px;
        }
        .logo_wrapper img {
          height: 80px;
        }
        .main-title {
          font-size: 2.4rem;
          font-weight: bold;
          color: #fff;
          margin-bottom: 2rem;
          text-align: center;
        }
             .loader_wrapper{
            height: 100vh;
            width: 100vw;
            background-color: #ffffffb2;
            position: fixed;
            top: 0;
            left: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            }
            .loader_wrapper img{
                height: 70px;
                animation: loader .8s infinite linear;
        }
        @keyframes loader{
            from{
                transform: rotate(0deg);
            }
            to{
                transform: rotate(360deg);
            }
        }
        .form-container {
          max-width: 48rem;
          margin: 0 auto;
        }
        .form-card {
          background: white;
          border-radius: 0.5rem;
          box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1);
          padding: 2rem;
          min-width: 660px;
        }
        .form-label {
          display: block;
          font-size: 0.875rem;
          font-weight: 500;
          color: #374151;
          margin-bottom: 0.5rem;
          line-height: 1.5;
        }
        .form-input {
          width: 100%;
          padding: 0.75rem 1rem;
          border: 1px solid #d1d5db;
          border-radius: 0.5rem;
          font-size: 1rem;
          transition: all 0.2s ease;
          box-sizing: border-box;
        }
        select.form-input {
          appearance: none;
          -webkit-appearance: none;
          -moz-appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 0.75rem center;
          background-size: 1.8rem;
        }
        .form-input:focus {
          outline: none;
          border-color: transparent;
          box-shadow: 0 0 0 2px #6f8dff;
        }
        .form-input.error {
          border-color: #ef4444;
        }
        .nav-button.success:hover {
          background: #059669;
        }
           .nav-button {
          display: flex;
          align-items: center;
          padding: 0.75rem 1.5rem;
          border-radius: 0.5rem;
          font-weight: 500;
          border: none;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 1rem;
        }
           .nav-button.success {
          background-color: #059669;
          color: white;
        }
          .form-select{
                padding-right: 40px !important;
          }
                .required {
          color: #ef4444;
        }
          
        @media (max-width: 850px) {
  .form-card {
    min-width: 100%;
  }
  .main-title {
    font-size: 2.3rem;
  }
  .logo_wrapper img {
    height: 75px;
  }
  .logo_wrapper {
    margin-bottom: 0px;
  }
}

@media (max-width: 650px) {
  .main-title {
    font-size: 1.8rem;
  }
  .step-title {
    font-size: 1.6rem;
  }
  select.form-input {
    background-size: 1.5rem !important;
  }
}

@media (max-width: 470px) {
  .form-card {
    padding: 1.2rem;
  }
  .logo_wrapper img {
    height: 65px;
  }
  .main-title {
    font-size: 1.8rem;
    margin-bottom: 1.9rem;
  }
      .step-description{
    font-size: 15px; 
}
  .step-title {
    font-size: 1.4rem;
  }
  .form-input {
    font-size: 0.95rem;
  }
    .nav-button {
    font-size: 0.9rem;
    padding: 0.65rem 1.1rem;
  }
}



      `}</style>
        </div>
    );
};

export default BilingualExamForm;
